import React, { useState } from 'react';
import { Lock, LogIn, Database, AlertCircle, CheckCircle, FlaskConical } from 'lucide-react';
import { authService } from '../services/supabaseService';
import { isSupabaseConfigured } from '../lib/supabaseClient';

const AdminLogin = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const supabaseConnected = isSupabaseConfigured();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setLoading(true);

    try {
      const data = await authService.signIn(email.trim(), password);
      if (data?.session || data?.user) {
        onLogin(data.user, data.session);
      }
    } catch (err) {
      console.error('Auth error:', err);
      setError(err.message || 'Authentication failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  const handleDemoLogin = (e) => {
    e.preventDefault();
    const { user, session } = authService.enterDemoMode();
    onLogin(user, session);
  };

  return (
    <div className="auth-container">
      <div className="auth-card glass-panel" style={{ maxWidth: '440px', width: '100%' }}>
        <div className="auth-header">
          <div className="auth-logo">
            <Lock size={24} />
          </div>
          <h2>Admin Portal</h2>
          <p>Sign in to manage your PG</p>

          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.4rem',
            padding: '0.25rem 0.75rem',
            borderRadius: '999px',
            fontSize: '0.75rem',
            fontWeight: 500,
            marginTop: '0.75rem',
            backgroundColor: supabaseConnected ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
            color: supabaseConnected ? '#10b981' : '#ef4444',
            border: `1px solid ${supabaseConnected ? 'rgba(16, 185, 129, 0.25)' : 'rgba(239, 68, 68, 0.25)'}`
          }}>
            <Database size={12} />
            <span>{supabaseConnected ? 'Supabase Auth' : 'Supabase Disconnected'}</span>
          </div>
        </div>
        
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem' }}>
              Email Address
            </label>
            <div style={{ position: 'relative' }}>
              <input 
                type="email"
                placeholder="admin@example.com" 
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                style={{ width: '100%', boxSizing: 'border-box' }}
              />
            </div>
          </div>
          
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem' }}>Password</label>
            <input 
              type="password" 
              placeholder="Enter password" 
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              style={{ width: '100%', boxSizing: 'border-box' }}
            />
          </div>
          
          {error && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              color: 'var(--danger-color, #ef4444)',
              backgroundColor: 'rgba(239, 68, 68, 0.1)',
              padding: '0.65rem 0.85rem',
              borderRadius: '6px',
              fontSize: '0.85rem',
              border: '1px solid rgba(239, 68, 68, 0.2)'
            }}>
              <AlertCircle size={16} style={{ flexShrink: 0 }} />
              <span>{error}</span>
            </div>
          )}

          {message && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              color: 'var(--success-color, #10b981)',
              backgroundColor: 'rgba(16, 185, 129, 0.1)',
              padding: '0.65rem 0.85rem',
              borderRadius: '6px',
              fontSize: '0.85rem',
              border: '1px solid rgba(16, 185, 129, 0.2)'
            }}>
              <CheckCircle size={16} style={{ flexShrink: 0 }} />
              <span>{message}</span>
            </div>
          )}

          {!supabaseConnected && (
            <div style={{
              fontSize: '0.8rem',
              color: '#f59e0b',
              background: 'rgba(245, 158, 11, 0.1)',
              padding: '0.65rem 0.85rem',
              borderRadius: '6px',
              border: '1px solid rgba(245, 158, 11, 0.2)'
            }}>
              ⚠️ <strong>Supabase credentials not configured:</strong> Please configure <code>REACT_APP_SUPABASE_URL</code> &amp; <code>REACT_APP_SUPABASE_ANON_KEY</code> in your <code>.env</code> file to enable authentication.
            </div>
          )}
          
          <button 
            type="submit" 
            className="btn-primary mt-2"
            disabled={loading || !supabaseConnected}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', opacity: !supabaseConnected ? 0.6 : 1 }}
          >
            {loading ? (
              <span>Authenticating...</span>
            ) : (
              <>
                <LogIn size={18} />
                <span>Sign In</span>
              </>
            )}
          </button>

          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', margin: '0.5rem 0' }}>
            <div style={{ flex: 1, height: '1px', backgroundColor: 'var(--border-color, rgba(255,255,255,0.1))' }}></div>
            <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary, #94a3b8)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>OR</span>
            <div style={{ flex: 1, height: '1px', backgroundColor: 'var(--border-color, rgba(255,255,255,0.1))' }}></div>
          </div>

          <button
            type="button"
            onClick={handleDemoLogin}
            style={{
              padding: '0.75rem 1rem',
              borderRadius: '0.5rem',
              border: '1px dashed rgba(245, 158, 11, 0.45)',
              backgroundColor: 'rgba(245, 158, 11, 0.08)',
              color: '#f59e0b',
              fontWeight: '600',
              fontSize: '0.9rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem',
              transition: 'all 0.2s ease'
            }}
            title="Explore the app in an isolated sandbox with demo tables and hidden expenses"
          >
            <FlaskConical size={18} />
            <span>Enter Demo Mode (Sandbox)</span>
          </button>
        </form>
      </div>
    </div>
  );
};

export default AdminLogin;


